import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { solutionDashboardKeys } from "@/lib/query";

export interface DeleteSolutionPreview {
  id: string;
  name: string;
  accountName: string;
  status: string;
  assignments: number;
  pods: number;
  snapshots: number;
}

export interface DeleteSolutionResult {
  id: string;
  ok: boolean;
  error?: string;
}

async function postJson<T>(url: string, ids: string[]): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function useDeleteSolutionsPreview(ids: string[], enabled: boolean) {
  return useQuery<DeleteSolutionPreview[]>({
    queryKey: solutionDashboardKeys.deletePreview(ids),
    queryFn: async () =>
      (
        await postJson<{ previews: DeleteSolutionPreview[] }>(
          "/api/dashboard/solutions/delete-preview",
          ids,
        )
      ).previews,
    enabled: enabled && ids.length > 0,
  });
}

export function useDeleteSolutions() {
  const qc = useQueryClient();
  return useMutation<{ results: DeleteSolutionResult[] }, Error, string[]>({
    mutationFn: (ids) =>
      postJson<{ results: DeleteSolutionResult[] }>("/api/dashboard/solutions/delete", ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
    },
  });
}
